import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'hooks/reactRedux';
import { setUserPreference } from 'redux/actionCreators';

import { usePrevious } from 'utils/reactHooks';

import Autocomplete, { Props } from './Autocomplete';

type ShouldSavePreferenceKey =
  | 'flexShouldSavePoolSelection'
  | 'flexShouldSaveClusterSelection';
type lastUsedPreferenceKey =
  | 'flexLastUsedPoolSelection'
  | 'flexLastUsedClusterSelection';

type PreferenceProps<T> = Omit<Props<T>, 'onSelected'> & {
  shouldSavePreferenceKey: ShouldSavePreferenceKey;
  lastUsedPreferenceKey: lastUsedPreferenceKey;
  onSelected: (option: T | null) => void;
};

export default function AutocompleteWithPreference<T extends { label: string }>(
  props: PreferenceProps<T>
) {
  const {
    shouldSavePreferenceKey,
    lastUsedPreferenceKey,
    onSelected,
    ...rest
  } = props;
  const { preferences } = useSelector((state) => state);
  const dispatch = useDispatch();
  const shouldSavePreference = preferences[shouldSavePreferenceKey];
  const lastUsedPreference = preferences[lastUsedPreferenceKey];
  const lastUsedPreferenceOption =
    props.options.find(
      (o) =>
        o[props.idProperty || 'id'] ===
        parseInt(lastUsedPreference as string, 10)
    ) || null;
  const [selectedOption, setSelectedOption] = useState<T | null>(
    shouldSavePreference && lastUsedPreference ? lastUsedPreferenceOption : null
  );

  const saveSelection = (value: string | number | null) => {
    dispatch(
      setUserPreference({
        key: lastUsedPreferenceKey,
        value
      })
    );
  };

  useEffect(() => {
    if (shouldSavePreference && lastUsedPreferenceOption) {
      onSelected(lastUsedPreferenceOption);
    }
  }, []);

  const prevShouldSaveSelection = usePrevious(shouldSavePreference);
  useEffect(() => {
    if (
      prevShouldSaveSelection !== undefined &&
      prevShouldSaveSelection !== shouldSavePreference
    ) {
      if (shouldSavePreference) {
        if (selectedOption) {
          saveSelection(selectedOption[props.idProperty || 'id']);
        }
      } else {
        saveSelection(null);
      }
    }
  }, [prevShouldSaveSelection, shouldSavePreference]);

  return (
    <Autocomplete
      {...rest}
      value={selectedOption}
      onSelected={(option) => {
        setSelectedOption(option || null);
        if (shouldSavePreference) {
          saveSelection(option ? option[props.idProperty || 'id'] : null);
        }
        onSelected(option || null);
      }}
    />
  );
}
